import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { pageDuPlus } from './lePlusSelonLaPage';
import { useDeconnexion, useEmailConnecte } from '../partage/useArbre';
import { Capture } from './Capture';
import { ParametresDuTelephone } from './ParametresDuTelephone';
import { noterCetAppareilVu } from './cetAppareil';
import type { PanneauDuPouce } from './panneauDuPouce';
import { ContexteDuHaut, initialeDe } from '../partage/cadreDuHaut';
import { usePrenom } from '../profil';
import { AGENDA, JOURNEE, MAINTENANT, PARAMETRES, PROJETS } from '../partage/vocabulaire';
import './CadreDuTelephone.css';

/**
 * Le cadre du téléphone, monté par le cadre commun (`cadre/Cadre.tsx`) sous 720 px : la page ou
 * un panneau au milieu, et la navigation **en bas** : c'est la seule zone que le pouce atteint
 * sans que la main change de prise.
 *
 * Le haut de chaque écran est à l'écran lui-même (`partage/HautDuPouce.tsx`, chantier
 * telephone-habit, 02) : le cadre lui donne l'initiale et le geste qui ouvre les Paramètres de
 * l'app (`ContexteDuHaut`).
 *
 * Il vivait avec la barre de l'ordinateur dans un seul `Cadre.tsx` ; séparé au chantier
 * `navigation` (04), sans rien changer à l'écran.
 *
 * Couché, toute page (`couche`, chantier telephone-couche-suite, 01, maquette `rendre()`, la branche
 * `etat.couche`, `.rail`) : les mêmes cinq boutons, en rail de 84 px à gauche, l'initiale en
 * tête. L'initiale ouvre les mêmes Paramètres du téléphone, droit comme couché, en deux colonnes
 * couché (chantier telephone-couche-suite, 05).
 */
export function CadreDuTelephone({ children, couche = false }: { children: React.ReactNode; couche?: boolean }) {
  const deconnexion = useDeconnexion();
  const email = useEmailConnecte();
  // Le + ouvert : `true` là où l'on est, ou déjà dans une bulle (l'arbre d'un projet).
  const [capture, setCapture] = useState<boolean | { dans: string }>(false);
  // Les Paramètres du téléphone, ouverts par ⚙ : un panneau du cadre, pas une route.
  const [panneau, setPanneau] = useState<PanneauDuPouce>(null);
  const fermerLePanneau = () => setPanneau(null);

  // Un appareil abonné aux notifications dit au serveur qu'il est vu (son fuseau), une fois par
  // ouverture de l'app (chantier notifications-socle, ticket 03), sans rien attendre.
  useEffect(() => {
    void noterCetAppareilVu();
  }, []);

  // Là où l'on est : le + sait déjà ce qu'il ajoute et où, sans qu'on ait à le lui dire
  // (chantier `telephone-projets-fiche`, ticket 05, `lePlusSelonLaPage.ts`).
  const ici = useLocation();
  const naviguer = useNavigate();

  // L'initiale du haut de chaque écran : le prénom du Profil, sinon l'e-mail.
  const prenom = usePrenom();
  const initiale = initialeDe(prenom, email);
  const haut = useMemo(
    () => ({
      initiale,
      ouvrirLesParametres: () => setPanneau('parametres'),
      ouvrirLePlusDans: (bulleId: string) => setCapture({ dans: bulleId }),
    }),
    [initiale]
  );

  // Les Paramètres du téléphone, à la place de la page, droit comme couché.
  const parametres = panneau !== null && (
    <ParametresDuTelephone
      panneau={panneau}
      email={email}
      prenom={prenom}
      couche={couche}
      onAller={setPanneau}
      // Le Profil garde d'où l'on vient : son retour y ramène, les Paramètres ouverts dessus.
      onProfil={() => {
        setPanneau(null);
        naviguer('/profil', { state: { depuis: ici.pathname === '/profil' ? '/maintenant' : `${ici.pathname}${ici.search}` } });
      }}
      // Sortir désabonne aussi cet appareil des notifications (`useDeconnexion`).
      onSortir={() => deconnexion.mutate()}
    />
  );

  const boutons = (
    <>
      <NavLink to="/maintenant" data-tuto="menu-maintenant" onClick={fermerLePanneau}>
        <IconeMaintenant />
        {MAINTENANT}
      </NavLink>
      {/* Un projet ouvert est encore dans les Projets : leur bouton reste celui de l'écran en cours. */}
      <NavLink
        to="/dossiers"
        data-tuto="menu-projets"
        className={({ isActive }) => (isActive || ici.pathname.startsWith('/feuille/') ? 'active' : '')}
        onClick={fermerLePanneau}
      >
        <IconeDossiers />
        {PROJETS}
      </NavLink>

      {/*
        Ajouter depuis n'importe quel écran, là où l'on est (ticket 05 de
        telephone-projets-fiche). C'est le geste le plus fréquent de l'app et le seul dont
        l'échec fait perdre l'idée (docs/adr/0001).
      */}
      <button
        type="button"
        className="onglets-capturer"
        onClick={() => setCapture(true)}
        aria-label="capturer une bulle"
      >
        <span className="onglets-capturer-rond">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
            <path d="M12 5v14M5 12h14" />
          </svg>
        </span>
      </button>

      <NavLink to="/agenda" data-tuto="menu-agenda" onClick={fermerLePanneau}>
        <IconeAgenda />
        {AGENDA}
      </NavLink>
      <NavLink to="/journee" data-tuto="menu-journee" onClick={fermerLePanneau}>
        <IconeJournee />
        {JOURNEE}
      </NavLink>
    </>
  );

  const lePlus = capture && (
    <Capture
      page={typeof capture === 'object' ? { sorte: 'feuille', id: capture.dans } : pageDuPlus(ici.pathname, ici.search)}
      onFermer={() => setCapture(false)}
    />
  );

  if (couche) {
    return (
      <ContexteDuHaut.Provider value={haut}>
        <nav className="onglets onglets--rail" aria-label="les écrans du téléphone">
          {initiale && (
            <button
              type="button"
              className="haut-pouce-moi onglets-moi"
              aria-label={PARAMETRES}
              title={PARAMETRES}
              onClick={haut.ouvrirLesParametres}
            >
              {initiale}
            </button>
          )}
          {boutons}
        </nav>
        {/* La même clé, droit ou couché : tourner le téléphone garde la page et la fiche ouverte. */}
        <main key="corps" className="cadre-corps">
          {parametres || children}
        </main>
        {lePlus}
      </ContexteDuHaut.Provider>
    );
  }

  return (
    <ContexteDuHaut.Provider value={haut}>
      <main key="corps" className="cadre-corps">
        {parametres || children}
      </main>

      {/*
        Au pouce, la navigation passe EN BAS : c'est la seule zone que le pouce atteint sans
        que la main change de prise. Toujours cinq boutons, dans l'ordre de la maquette
        (chantier telephone-cadre, ticket 01, décision d'Igor du 2026-10-05,
        `design/telephone.html`, la barre `.tabs`) : Maintenant, Projets, +, Agenda, Journée.
      */}
      <nav className="onglets" aria-label="les écrans du téléphone">
        {boutons}
      </nav>

      {lePlus}
    </ContexteDuHaut.Provider>
  );
}

/**
 * Les icônes de la barre du bas, celles de la maquette du carnet (`app-au-telephone.html`, `ic`) :
 * un éclair pour Maintenant, un carnet pour les Projets, un agenda, une page de journée.
 */
const TRAIT = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.9, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;

function IconeMaintenant() {
  return (
    <svg viewBox="0 0 24 24" {...TRAIT} aria-hidden="true">
      <path d="M13 3 5 14h6l-1 7 8-11h-6z" />
    </svg>
  );
}

function IconeDossiers() {
  return (
    <svg viewBox="0 0 24 24" {...TRAIT} aria-hidden="true">
      <path d="M5 4h11a3 3 0 0 1 3 3v13H8a3 3 0 0 1-3-3z" />
      <path d="M5 17a3 3 0 0 1 3-3h11" />
    </svg>
  );
}

function IconeAgenda() {
  return (
    <svg viewBox="0 0 24 24" {...TRAIT} strokeLinejoin="miter" aria-hidden="true">
      <rect x="4" y="5" width="16" height="15" rx="2" />
      <path d="M4 9h16M9 3v4M15 3v4" />
    </svg>
  );
}

function IconeJournee() {
  return (
    <svg viewBox="0 0 24 24" {...TRAIT} strokeLinejoin="miter" aria-hidden="true">
      <path d="M6 4h12v16H6z" />
      <path d="M9 8h6M9 12h6M9 16h3" />
    </svg>
  );
}
